import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle, ArrowLeft, CheckSquare } from 'lucide-react';
import api from '../utils/api';


const SubtaskCompletionModal = ({ 
  isOpen, 
  onClose, 
  task, 
  incompleteSubtasks = [], 
  newStatus, 
  previousStatus,
  onConfirmComplete, 
  onCancel 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleMarkAllComplete = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      
      // Use bulk completion endpoint
      const subtaskIds = incompleteSubtasks.map(subtask => subtask.id);
      
      await api.put(`/subtasks/bulk-complete`, 
        subtaskIds,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Now proceed with task completion
      onConfirmComplete();
      
    } catch (error) {
      console.error('Error completing subtasks:', error);
      setError('Failed to complete subtasks. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Incomplete Subtasks Found
                </h2>
                <p className="text-orange-100 text-sm">
                  This task has unfinished subtasks
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-orange-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Task: {task.title}
            </h3>
            <p className="text-gray-600 mb-4">
              You're trying to mark this task as "<span className="font-semibold text-green-600">{newStatus}</span>", 
              but it has <span className="font-semibold text-orange-600">{incompleteSubtasks.length}</span> incomplete subtask{incompleteSubtasks.length !== 1 ? 's' : ''}.
            </p>
          </div>

          {/* Incomplete Subtasks List */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-orange-900 mb-3 flex items-center">
              <CheckSquare className="w-4 h-4 mr-2" />
              Incomplete Subtasks ({incompleteSubtasks.length})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {incompleteSubtasks.map((subtask, index) => (
                <div key={subtask.id} className="flex items-center space-x-3 p-2 bg-white rounded border border-orange-100">
                  <div className="w-5 h-5 border-2 border-orange-300 rounded flex items-center justify-center">
                    <span className="text-orange-600 text-xs font-bold">{index + 1}</span>
                  </div>
                  <span className="text-gray-800 text-sm flex-1">
                    {subtask.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">What would you like to do?</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-3 bg-green-100 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">Mark All Subtasks Complete</p>
                  <p className="text-xs text-green-700 mt-1">
                    Complete all {incompleteSubtasks.length} subtask{incompleteSubtasks.length !== 1 ? 's' : ''} and mark the task as "{newStatus}"
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 bg-gray-100 border border-gray-200 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Cancel Task Completion</p>
                  <p className="text-xs text-gray-700 mt-1">
                    Keep the task as "{previousStatus}" and finish the subtasks manually
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-600 text-sm flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                {error}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 inline mr-2" />
              Cancel
            </button>
            <button
              onClick={handleMarkAllComplete}
              disabled={isProcessing}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <CheckCircle className="w-4 h-4" />
              <span>
                {isProcessing ? 'Completing...' : `Complete All & Mark ${newStatus}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubtaskCompletionModal;